import { IMAGE_FILE_EXTENSIONS } from '../constants/AddPatientNoteModal.constants';

export const getFileExtension = (fileName: string): string => {
  if (!fileName) return '';
  const lastDotIndex = fileName.lastIndexOf('.');
  if (
    lastDotIndex === -1 ||
    lastDotIndex === 0 ||
    lastDotIndex === fileName.length - 1
  ) {
    return '';
  }
  return fileName.slice(lastDotIndex + 1);
};

export const getFileNameWithoutExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.');
  if (fileName[0] === '.' && dotIndex === 0) {
    return fileName.slice(1);
  }
  if (dotIndex !== -1) {
    return fileName.slice(0, dotIndex);
  }
  return fileName;
};

/**
 * Checks if a URL is a blob URL or data URL
 * @param url - The URL string to check
 * @returns true if the URL starts with 'blob:' or 'data:', false otherwise
 */
export const isBlobOrDataUrl = (url: string): boolean =>
  typeof url === 'string' &&
  (url.startsWith('blob:') || url.startsWith('data:'));

/**
 * Converts a blob URL or data URL to a File object
 * @param url - The blob: or data: URL to convert
 * @param fileName - The desired file name for the resulting File
 * @returns A Promise that resolves to a File object or null if conversion fails
 */
const urlToFile = async (
  url: string,
  fileName: string,
): Promise<File | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type });
  } catch (error) {
    console.error('Failed to convert URL to file:', error);
    return null;
  }
};

const triggerFileDownload = async (
  url: string,
  fileName: string,
): Promise<void> => {
  if (!url) return;
  let blobURL: string | null = null;
  try {
    const response = await fetch(url, {
      headers: { 'Cache-Control': 'no-cache' },
    });
    const data = await response.arrayBuffer();
    blobURL = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = blobURL;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to download file:', error);
    globalThis.window.open(url, '_blank', 'noopener');
  } finally {
    if (blobURL) {
      globalThis.URL.revokeObjectURL(blobURL);
    }
  }
};

const fileUtil = {
  canShowPreview: (fileName: string): boolean =>
    IMAGE_FILE_EXTENSIONS.includes(getFileExtension(fileName)) ||
    fileName.endsWith('.pdf'),
  getFileExtension,
  getFileNameWithoutExtension,
  isAllowedFileSize: (file: File, maxSizeInMB: number): boolean => {
    if (!file) return false;
    const sizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= sizeInBytes;
  },
  isImageFile: (fileName: string): boolean =>
    IMAGE_FILE_EXTENSIONS.includes(getFileExtension(fileName)),
  triggerFileDownload,
  urlToFile,
  validateFileName: (titleStr: string): boolean => {
    if (titleStr === null || titleStr === undefined) {
      return false;
    }
    const validFileNameRegex = /^(?! +$)[a-zA-Z0-9\s\-_().]+$/;
    return validFileNameRegex.test(titleStr);
  },
};

export default fileUtil;
